import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  SyntheticEvent,
  TouchEventHandler,
} from 'react'
import { useMemo } from 'react'
import { useEffect } from 'react'
import { useCallback, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { thumbHashToDataURL } from 'thumbhash'

import { useNavigateRef } from '@jotta/hooks'
import type { Avatar as AvatarType } from '@jotta/types/Avatar'
import { Avatar } from '@jotta/ui/Avatar'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { CommentsBubble } from '@jotta/ui/CommentsBubble'
import SvgFillPlayPreview from '@jotta/ui/SvgFillPlayPreview'
import { cn } from '@jotta/utils/css'

import type { CollectionRef } from '../../api/useCollection'
import { useSelectionStore } from '../../store/PhotoContext'
import type { SelectablePhoto } from '../../store/SelectionStore'
import { MediaThumbActionsMenuButton } from '../MediaThumbActionsMenu/MediaThumbActionsMenuButton'
import { PhotoCheckbox } from '../PhotoCheckbox/PhotoCheckbox'

import { SearchSimilarButton } from './SearchSimilarButton'

import styles from './PhotoGallery.module.scss'

export interface MediaThumbProps {
  src: string
  className?: string
  comments?: number
  showOwnerAvatar?: boolean
  ownerAvatar?: AvatarType
  filename: string
  photo: SelectablePhoto
  duration?: string
  playIcon?: boolean
  showSimilarSearch?: boolean
  title?: string
  onNavigate?: (id: string) => void
  thumbHash?: Uint8Array
  collectionRef?: CollectionRef
}

export const MediaThumb = observer<MediaThumbProps>(function MediaThumb({
  src,
  className,
  comments = 0,
  showOwnerAvatar,
  ownerAvatar,
  filename,
  photo,
  duration,
  playIcon,
  showSimilarSearch,
  title,
  onNavigate,
  thumbHash,
  collectionRef,
}) {
  const id = photo.id
  const selection = useSelectionStore()
  const { onClickToggleIfActive, onClickToggle, onToggleSelect } = selection

  const navigate = useNavigateRef()

  const activateMedia = useCallback(
    (e: SyntheticEvent<HTMLDivElement>, blurBeforeNavigate = false) => {
      const current = e.currentTarget
      const target = e.target as HTMLElement | null

      if (!target || !current.contains(target)) {
        return
      }

      // Defensive guard for nested controls.
      if (target.closest('[data-skip-click]')) {
        return
      }

      if (onClickToggleIfActive(photo, e)) {
        return
      }

      if (blurBeforeNavigate) {
        current.blur()
      }

      if (onNavigate) {
        onNavigate(id)
      } else {
        navigate.current(`./${id}`)
      }
    },
    [onClickToggleIfActive, navigate, id, photo, onNavigate],
  )

  const handleClick = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      activateMedia(e, true)
    },
    [activateMedia],
  )

  const handleKeyDown = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null

    if (
      (e.key !== 'Enter' && e.key !== ' ') ||
      target !== e.currentTarget ||
      target?.closest('[data-skip-click]')
    ) {
      return
    }

    e.preventDefault()
  }, [])

  const handleKeyUp = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement | null

      if (
        (e.key !== 'Enter' && e.key !== ' ') ||
        target !== e.currentTarget ||
        target?.closest('[data-skip-click]')
      ) {
        return
      }

      e.preventDefault()
      activateMedia(e, true)
    },
    [activateMedia],
  )

  const onToggleSelectHandler = useCallback(
    (selected: boolean, e: SyntheticEvent) =>
      onToggleSelect(photo, selected, e),
    [photo, onToggleSelect],
  )

  const isSelected = selection.has(id)
  const actionCollectionRef = collectionRef?.ref?.case ? collectionRef : null

  const timerRef = useRef(0)

  const startTimeout = useCallback<TouchEventHandler>(
    e => {
      timerRef.current = window.setTimeout(() => {
        onClickToggle(photo, e)
      }, 400)
    },
    [photo, onClickToggle],
  )

  const cancelTimeout = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = 0
    }
  }, [])

  useEffect(() => {
    return cancelTimeout
  }, [startTimeout, cancelTimeout])

  const thumbHashDataUrl = useMemo(
    () => (thumbHash ? thumbHashToDataURL(thumbHash) : undefined),
    [thumbHash],
  )
  return (
    <div
      role="button"
      data-testid="MediaThumb"
      className={cn(styles.mediaWrapper, 'group', className)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onTouchStart={startTimeout}
      onTouchEnd={cancelTimeout}
      onTouchMove={cancelTimeout}
      data-photoid={id}
      draggable={false}
      tabIndex={0}
    >
      <img
        className="block h-full w-full bg-cover object-cover object-center text-transparent"
        alt={filename}
        title={title}
        src={src}
        data-filename={filename}
        loading="lazy"
        draggable={false}
        style={
          thumbHashDataUrl
            ? {
                backgroundImage: `url("${thumbHashDataUrl}")`,
              }
            : undefined
        }
      />

      {playIcon && (
        <PlainBrandIcon
          className={styles.playButton}
          icon={SvgFillPlayPreview}
        />
      )}

      {duration && <div className={styles.duration}>{duration}</div>}

      <div
        className={cn(
          'absolute inset-0 size-full p-2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100',
          { 'bg-photo-overlay opacity-100': isSelected },
        )}
      >
        <PhotoCheckbox
          isSelected={isSelected}
          onToggleSelect={onToggleSelectHandler}
        />
      </div>
      {showSimilarSearch && <SearchSimilarButton photoId={id} />}

      {actionCollectionRef && (
        <div
          className="pointer-events-none absolute right-[6px] top-[6px] opacity-0 transition-opacity duration-200 has-[button[data-state=open]]:pointer-events-auto has-[button[data-state=open]]:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100"
          data-skip-click
        >
          <MediaThumbActionsMenuButton
            photo={photo}
            collectionRef={actionCollectionRef}
          />
        </div>
      )}

      {showOwnerAvatar && ownerAvatar ? (
        <div className="pointer-events-none absolute bottom-2 right-2 opacity-0 transition-opacity duration-200 group-hover:pointer-events-auto group-hover:opacity-100">
          <Avatar
            className="border border-solid border-surface-container-low"
            avatar={ownerAvatar}
          />
        </div>
      ) : null}

      {comments > 0 && <CommentsBubble count={comments} />}
    </div>
  )
})
